import { useQuery } from '@tanstack/react-query';
import { LocaleLink as Link } from '../i18n/locale-link';
import { useT } from '../i18n/locale-context';
import { useAuth } from '../lib/auth-context';
import { listSavedSearches } from '../lib/saved-search-client';
import { BellIcon, ArrowRight } from './icons';

/**
 * "Add a category" nudge — the single highest-leverage prompt for engagement.
 * Most Telegram/email-reachable users never receive alerts simply because they
 * have not added a category (a saved search): alerts only fire for saved
 * searches, so a user with zero saved searches has nothing to be notified about.
 *
 * Shown ONLY to a signed-in user with NO saved searches; disappears the moment
 * they add one. Two placements:
 *  - `dashboard` (default): a CTA button deep-links to the catalog.
 *  - `catalog`: no nav button (they're already here) — it points them at the
 *    "Категория" filter directly above, where picking a category surfaces the
 *    "save search" pill that turns it into an alert.
 *
 * Auth-aware: on the public catalog an anonymous visitor renders nothing (and
 * the saved-searches query never fires, so no 401).
 */
export function AddCategoryNudge({ variant = 'dashboard' }: { variant?: 'dashboard' | 'catalog' }) {
  const t = useT();
  const { status } = useAuth();
  const authed = status === 'authed';

  const { data: searches, isLoading } = useQuery({
    queryKey: ['saved-searches'],
    queryFn: listSavedSearches,
    enabled: authed,
  });

  // Only for signed-in users; don't flash before we know; hide once a search exists.
  if (!authed || isLoading || (searches && searches.length > 0)) return null;

  // Catalog: a compact inline hint that sits directly above the Category + Save
  // line — not a bulky banner.
  if (variant === 'catalog') {
    return (
      <p className="flex items-center gap-1.5 text-[12.5px] font-semibold text-orange-ink">
        <BellIcon className="h-4 w-4 shrink-0" />
        <span>{t('catalog.nudge.compact')}</span>
      </p>
    );
  }

  // Dashboard: the full banner with a deep-link CTA.
  return (
    <div className="relative overflow-hidden rounded-lg border border-brand/25 bg-brand-dim p-5 shadow-m-sm sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-4">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand text-white">
            <BellIcon className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h2 className="mb-1 font-brand text-[16px] font-extrabold tracking-[-0.2px] text-ink">
              {t('dashboard.nudge.title')}
            </h2>
            <p className="text-[13.5px] leading-[1.55] text-ink-soft">
              {t('dashboard.nudge.body')}
            </p>
          </div>
        </div>
        <Link
          to="/catalog"
          className="inline-flex h-[42px] shrink-0 items-center justify-center gap-1.5 rounded-md bg-brand px-5 text-[13px] font-bold text-white transition-colors hover:bg-brand-mid"
        >
          {t('dashboard.nudge.cta')}
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}
